/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Modal from '../../../../components/Modal/Modal';
import { PrimaryButton, GhostButton } from '../../../../styles/ui/Button';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field, RequiredMark } from '../../../../styles/forms/Field';
import { listJobOpenings } from '../../../../services/hr/recruitment/jobOpeningsApi';
import { pullCandidateToOpening } from '../../../../services/hr/recruitment/candidatesApi';
import { JOB_OPENING_STATUS } from '../constants';
import { readApiError, recruitmentErrorMessage } from '../utils/apiError';

// ---------------------------------------------------------------------------
// Puxar candidato pra vaga (BANCO_CURRICULOS → ENTREVISTA_1). Só vagas ABERTAS
// entram no seletor. O stage muda no backend. Trata os guards do handoff 0061:
// candidate_already_in_process, candidate_wrong_stage, job_opening_not_open.
// ---------------------------------------------------------------------------

const FieldError = styled.span`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.danger ?? '#b71c1c'};
   margin-top: 2px;
`;

function openingLabel(opening) {
   const position = opening?.job_position?.position_name;
   const title = opening?.title;
   if (title && position) return `${title} — ${position}`;
   return title || position || `Vaga #${opening?.id}`;
}

export default function PullToOpeningModal({
   isOpen,
   onClose,
   candidate,
   onSaved,
}) {
   const [openings, setOpenings] = useState([]);
   const [isLoadingOpenings, setIsLoadingOpenings] = useState(false);
   const [selectedOpeningId, setSelectedOpeningId] = useState('');
   const [fieldError, setFieldError] = useState(null);
   const [isSubmitting, setIsSubmitting] = useState(false);

   // Carrega as vagas ABERTAS toda vez que o modal abre.
   useEffect(() => {
      if (!isOpen) return undefined;

      let ignore = false;
      setSelectedOpeningId('');
      setFieldError(null);
      setIsSubmitting(false);
      setIsLoadingOpenings(true);

      listJobOpenings({ status: JOB_OPENING_STATUS.ABERTA, perPage: 100 })
         .then((result) => {
            if (!ignore) setOpenings(result.jobOpenings);
         })
         .catch(() => {
            if (!ignore) toast.error('Erro ao carregar as vagas abertas.');
         })
         .finally(() => {
            if (!ignore) setIsLoadingOpenings(false);
         });

      return () => {
         ignore = true;
      };
   }, [isOpen]);

   const handleConfirm = async () => {
      if (isSubmitting) return;
      if (!selectedOpeningId) {
         setFieldError('Selecione uma vaga.');
         return;
      }

      setIsSubmitting(true);
      setFieldError(null);
      try {
         const { message } = await pullCandidateToOpening(
            candidate.id,
            Number(selectedOpeningId),
         );
         toast.success(message);
         onSaved?.();
         onClose?.();
      } catch (err) {
         const { fieldErrors } = readApiError(err);
         if (fieldErrors.job_opening_id) {
            setFieldError(fieldErrors.job_opening_id);
         } else {
            toast.error(
               recruitmentErrorMessage(err, 'Erro ao puxar o candidato.'),
            );
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   const footer = (
      <>
         <GhostButton type="button" onClick={onClose} disabled={isSubmitting}>
            Cancelar
         </GhostButton>
         <PrimaryButton
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || isLoadingOpenings}
         >
            {isSubmitting ? 'Puxando…' : 'Puxar para a vaga'}
         </PrimaryButton>
      </>
   );

   const hasOpenings = openings.length > 0;

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title="Puxar candidato para vaga"
         subtitle={candidate ? candidate.full_name : ''}
         maxWidth="520px"
         footer={footer}
      >
         <p style={{ margin: '0 0 14px', fontSize: 13, color: '#555' }}>
            O candidato entra na esteira da vaga na <strong>Entrevista 1</strong>.
            Apenas vagas <strong>abertas</strong> podem receber candidatos.
         </p>

         <FormGrid>
            <Field className="col-12">
               <label htmlFor="pull-opening">
                  Vaga
                  <RequiredMark>*</RequiredMark>
               </label>
               <select
                  id="pull-opening"
                  value={selectedOpeningId}
                  onChange={(e) => {
                     setSelectedOpeningId(e.target.value);
                     setFieldError(null);
                  }}
                  disabled={isSubmitting || isLoadingOpenings}
                  aria-invalid={!!fieldError || undefined}
               >
                  <option value="">
                     {isLoadingOpenings ? 'Carregando…' : 'Selecione…'}
                  </option>
                  {openings.map((opening) => (
                     <option key={opening.id} value={String(opening.id)}>
                        {openingLabel(opening)}
                     </option>
                  ))}
               </select>
               {fieldError && <FieldError>{fieldError}</FieldError>}
               {!isLoadingOpenings && !hasOpenings && (
                  <FieldError>
                     Nenhuma vaga aberta. Cadastre/abra uma vaga primeiro.
                  </FieldError>
               )}
            </Field>
         </FormGrid>
      </Modal>
   );
}

PullToOpeningModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   candidate: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      full_name: PropTypes.string,
   }),
   onSaved: PropTypes.func,
};
